import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import EntityConstants from "../../configs/EntityConstants";
import { POST, GET } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import FormFieldFooter from "../../common/FormFieldFooter/FormFieldFooter";
import TagInputSection from "../../common/TagInputSection/TagInputSection";
import { useTranslation } from "react-i18next";
import { isValidHttpUrl } from "../../utils/urlValidation";

const MAX_CHARS_PROGRAMME_NAME = 80;
const MAX_CHARS_DISPLAY_NAME = 200;
const MAX_CHARS_PROGRAMME_DESCRIPTION = 250;
const MAX_CHARS_COURSE_URL = 500;

const INPUT_BASE_CLASS =
  "border text-cfGlobalText text-sm rounded-lg focus:ring-cfButton focus:border-cfButton block w-full";
const INPUT_PADDING_CLASS = "p-2";

const getInputBorderClass = (hasError) =>
  hasError
    ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
    : "border-cfIssuedScreenSearchBorder";

export default function CreateProgrammeModal({
  isOpen,
  onClose,
  onProgrammeCreated,
  isEditMode = false,
  initialName = "",
  initialDescription = "",
  initialDisplayName = "",
  initialLearningOutcomes = [],
  initialSkills = [],
  initialCourseUrl = "",

  onUpdate,
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [courseUrl, setCourseUrl] = useState(initialCourseUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    name: "",
    description: "",
    displayName: "",
    courseUrl: "",
  });
  const [batchName, setBatchName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [learningOutcomes, setLearningOutcomes] = useState(
    initialLearningOutcomes,
  );
  const [currentLearningOutcome, setCurrentLearningOutcome] = useState("");
  const [skills, setSkills] = useState(initialSkills);
  const [currentSkill, setCurrentSkill] = useState("");

  useEffect(() => {
    if (isEditMode) {
      setName(initialName);
      setDescription(initialDescription);
      setDisplayName(initialDisplayName);
      setCourseUrl(initialCourseUrl || "");
      setLearningOutcomes(initialLearningOutcomes || []);
      setSkills(initialSkills || []);
    }
  }, [
    isEditMode,
    initialName,
    initialDescription,
    initialDisplayName,
    initialCourseUrl,
    initialLearningOutcomes,
    initialSkills,
  ]);

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      name: "",
      description: "",
      displayName: "",
      courseUrl: "",
    };

    if (!name.trim()) {
      newErrors.name = t("workspace:required_programme_name");
      valid = false;
    } else if (name.length > MAX_CHARS_PROGRAMME_NAME) {
      newErrors.name = `${t(
        "workspace:programme_name_description_1",
      )} ${MAX_CHARS_PROGRAMME_NAME} ${t(
        "workspace:programme_name_description_2",
      )}`;
      valid = false;
    }

    if (!description.trim()) {
      newErrors.description = t("workspace:required_description");
      valid = false;
    } else if (description.length > MAX_CHARS_PROGRAMME_DESCRIPTION) {
      newErrors.description = `${t(
        "workspace:description_description_1",
      )} ${MAX_CHARS_PROGRAMME_DESCRIPTION} ${t(
        "workspace:description_description_2",
      )}`;
      valid = false;
    }

    if (!displayName.trim()) {
      newErrors.displayName = t("workspace:display_name_required");
      valid = false;
    } else if (displayName.length > MAX_CHARS_DISPLAY_NAME) {
      newErrors.displayName = `${t(
        "workspace:programme_name_description_1",
      )} ${MAX_CHARS_DISPLAY_NAME} ${t("workspace:programme_name_description_2")}`;
      valid = false;
    }

    const trimmedCourseUrl = courseUrl.trim();
    if (trimmedCourseUrl) {
      if (trimmedCourseUrl.length > MAX_CHARS_COURSE_URL) {
        newErrors.courseUrl = `${t(
          "workspace:course_url_description_1",
        )} ${MAX_CHARS_COURSE_URL} ${t("workspace:programme_name_description_2")}`;
        valid = false;
      } else if (!isValidHttpUrl(trimmedCourseUrl)) {
        newErrors.courseUrl = t("workspace:invalid_course_url");
        valid = false;
      }
    }

    setErrors(newErrors);
    return valid;
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (
      value.length <= MAX_CHARS_PROGRAMME_NAME ||
      value.length < name.length
    ) {
      setName(value);
      setErrors({ ...errors, name: "" });
      setErrorMessage("");
    }
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_CHARS_PROGRAMME_DESCRIPTION) {
      setDescription(value);
      setErrors({ ...errors, description: "" });
      setErrorMessage("");
    }
  };

  const handleDisplayNameChange = (e) => {
    const value = e.target.value;
    if (
      value.length <= MAX_CHARS_DISPLAY_NAME ||
      value.length < displayName.length
    ) {
      setDisplayName(value);
      setErrors({ ...errors, displayName: "" });
      setErrorMessage("");
    }
  };

  const handleCourseUrlChange = (e) => {
    const value = e.target.value;
    if (
      value.length <= MAX_CHARS_COURSE_URL ||
      value.length < courseUrl.length
    ) {
      setCourseUrl(value);
      setErrors({ ...errors, courseUrl: "" });
      setErrorMessage("");
    }
  };

  const handleAddLearningOutcome = () => {
    if (
      currentLearningOutcome.trim() &&
      !learningOutcomes.includes(currentLearningOutcome.trim())
    ) {
      setLearningOutcomes([...learningOutcomes, currentLearningOutcome.trim()]);
      setCurrentLearningOutcome("");
    }
  };

  const handleRemoveLearningOutcome = (outcome) => {
    setLearningOutcomes(learningOutcomes.filter((item) => item !== outcome));
  };

  const handleLearningOutcomeKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddLearningOutcome();
    }
  };

  const handleAddSkill = () => {
    if (currentSkill.trim() && !skills.includes(currentSkill.trim())) {
      setSkills([...skills, currentSkill.trim()]);
      setCurrentSkill("");
    }
  };

  const handleRemoveSkill = (skill) => {
    setSkills(skills.filter((item) => item !== skill));
  };

  const handleSkillKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddSkill();
    }
  };

  const handlePasteItems = (e, setItems, items, splitByComma = false) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");

    const itemsList = pastedText
      .split(splitByComma ? /[\n,;]+/ : /\n+/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    const uniqueItems = [...new Set(itemsList)];
    const newItems = uniqueItems.filter((item) => !items.includes(item));

    if (newItems.length > 0) {
      setItems([...items, ...newItems]);
    }
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    setIsLoading(true);

    const payload = {
      programme_name: name,
      description: description,
      programme_display_name: displayName,
      learning_outcomes: learningOutcomes,
      skills: skills,
      course_url: courseUrl.trim(),
    };

    if (isEditMode && onUpdate) {
      onUpdate(
        name,
        description,
        displayName,
        learningOutcomes,
        skills,
        courseUrl.trim(),
      );
      setIsLoading(false);
      onClose();
      return;
    }

    POST(EntityConstants.POST_CREATE_PROGRAMME, payload)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          setErrorMessage(data);
          Toast({
            type: "error",
            title: t("workspace:error"),
            message: data.message || t("workspace:failed_create_programme"),
          });
        } else {
          Toast({
            type: "success",
            title: t("workspace:success"),
            message: t("workspace:success_create_programme"),
          });
          onProgrammeCreated();
          resetForm();
          onClose();
        }
      })
      .catch(() => {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: t("workspace:failed_create_programme"),
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const resetForm = () => {
    setName("");
    setDescription("");
    setDisplayName("");
    setCourseUrl("");

    setLearningOutcomes([]);
    setSkills([]);
    setCurrentLearningOutcome("");
    setCurrentSkill("");
    setErrorMessage("");
    setErrors({
      name: "",
      description: "",
      displayName: "",
      courseUrl: "",
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const fetchBatchName = () => {
    GET(EntityConstants.GET_BATCH_NAME)
      .then((response) => response.json())
      .then((data) => {
        if (data.batch_display_name) {
          setBatchName(data.batch_display_name);
        }
      })
      .catch(() => {
        console.error("Error fetching batch name");
      });
  };

  useEffect(() => {
    fetchBatchName();
  }, []);

  const charactersLeftLabel = t("workspace:characters_left");

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                id="create-programme-modal"
                className="w-full max-w-5xl transform rounded-2xl bg-cfSecondary p-4 text-left align-middle shadow-xl transition-all"
              >
                <form
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  <div className="flex justify-between items-start gap-4 mb-4">
                    <div className="min-w-0 pr-2">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold text-cfHeadingText"
                      >
                        {isEditMode
                          ? t("workspace:edit")
                          : t("workspace:create_new")}{" "}
                        {t("workspace:programme")}
                      </Dialog.Title>
                      <p className="text-xs text-cfGlobalText mt-1 leading-snug">
                        {isEditMode
                          ? t("workspace:edit_the_text")
                          : t("workspace:create_new_text")}{" "}
                        {t("workspace:programme")}{" "}
                        {isEditMode
                          ? `${t("workspace:details_text")} ${
                              batchName ? batchName : t("workspace:batch")
                            } ${t("workspace:certificate_text")}`
                          : `${t("workspace:to_organize_your")} ${
                              batchName ? batchName : t("workspace:batch")
                            } ${t("workspace:certificate_text")}`}
                      </p>
                    </div>
                    <button
                      type="button"
                      id="create-programme-modal-close"
                      className="text-cfGlobalText hover:text-cfHeadingText focus:outline-none text-lg leading-none shrink-0"
                      onClick={handleClose}
                    >
                      <span className="sr-only">Close</span>
                      &#10005;
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
                    <div>
                      <label
                        className="block mb-1.5 text-sm font-medium text-cfHeadingText after:content-['*'] after:text-red-500"
                        htmlFor="create-programme-modal-input-name"
                      >
                        {t("workspace:name")}
                      </label>
                      <input
                        type="text"
                        id="create-programme-modal-input-name"
                        className={`${INPUT_BASE_CLASS} ${INPUT_PADDING_CLASS} ${getInputBorderClass(errors.name)}`}
                        placeholder="e.g. M Tech"
                        value={name}
                        onChange={handleNameChange}
                      />
                      <FormFieldFooter
                        error={errors.name}
                        errorId="create-programme-modal-error-name"
                        charLeft={MAX_CHARS_PROGRAMME_NAME - name.length}
                        charactersLeftLabel={charactersLeftLabel}
                      />
                    </div>

                    <div>
                      <label
                        className="block mb-1.5 text-sm font-medium text-cfHeadingText after:content-['*'] after:text-red-500"
                        htmlFor="create-programme-modal-input-display-name"
                      >
                        {t("workspace:display_name")}
                      </label>
                      <input
                        type="text"
                        id="create-programme-modal-input-display-name"
                        className={`${INPUT_BASE_CLASS} ${INPUT_PADDING_CLASS} ${getInputBorderClass(errors.displayName)}`}
                        placeholder="e.g. Master of Technology"
                        value={displayName}
                        onChange={handleDisplayNameChange}
                      />
                      <FormFieldFooter
                        error={errors.displayName}
                        errorId="create-programme-modal-error-display-name"
                        charLeft={MAX_CHARS_DISPLAY_NAME - displayName.length}
                        charactersLeftLabel={charactersLeftLabel}
                      />
                    </div>

                    <div>
                      <label
                        className="block mb-1.5 text-sm font-medium text-cfHeadingText after:content-['*'] after:text-red-500"
                        htmlFor="create-programme-modal-input-description"
                      >
                        {t("workspace:description")}
                      </label>
                      <textarea
                        id="create-programme-modal-input-description"
                        rows="2"
                        className={`${INPUT_BASE_CLASS} ${INPUT_PADDING_CLASS} ${getInputBorderClass(errors.description)} resize-none`}
                        placeholder="e.g., M Tech Certificate program"
                        value={description}
                        onChange={handleDescriptionChange}
                      />
                      <FormFieldFooter
                        error={errors.description}
                        errorId="create-programme-modal-error-description"
                        charLeft={
                          MAX_CHARS_PROGRAMME_DESCRIPTION - description.length
                        }
                        charactersLeftLabel={charactersLeftLabel}
                      />
                    </div>

                    <div>
                      <label
                        className="block mb-1.5 text-sm font-medium text-cfHeadingText"
                        htmlFor="create-programme-modal-input-course-url"
                      >
                        {t("workspace:course_url")}
                      </label>
                      <input
                        type="url"
                        id="create-programme-modal-input-course-url"
                        className={`${INPUT_BASE_CLASS} ${INPUT_PADDING_CLASS} ${getInputBorderClass(errors.courseUrl)}`}
                        placeholder={t("workspace:course_url_placeholder")}
                        value={courseUrl}
                        onChange={handleCourseUrlChange}
                      />
                      <FormFieldFooter
                        error={errors.courseUrl}
                        errorId="create-programme-modal-error-course-url"
                        charLeft={MAX_CHARS_COURSE_URL - courseUrl.length}
                        charactersLeftLabel={charactersLeftLabel}
                        hint={
                          !errors.courseUrl
                            ? t("workspace:course_url_hint")
                            : undefined
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3 mt-2">
                    <TagInputSection
                      title={t("workspace:learning_outcomes")}
                      inputId="create-programme-modal-input-learning-outcome"
                      addButtonId="create-programme-modal-add-learning-outcome"
                      value={currentLearningOutcome}
                      onChange={(e) =>
                        setCurrentLearningOutcome(e.target.value)
                      }
                      onKeyDown={handleLearningOutcomeKeyDown}
                      onPaste={(e) =>
                        handlePasteItems(
                          e,
                          setLearningOutcomes,
                          learningOutcomes,
                        )
                      }
                      onAdd={handleAddLearningOutcome}
                      placeholder="e.g., Understand advanced algorithms"
                      items={learningOutcomes}
                      onRemove={handleRemoveLearningOutcome}
                      tagIdPrefix="create-programme-modal-tag-learning-outcome"
                      removeIdPrefix="create-programme-modal-remove-learning-outcome"
                      emptyText={t("workspace:no_learning_outcomes_added")}
                      hintText={t("workspace:add_learning_outcomes_hint")}
                      addLabel={t("workspace:add")}
                    />
                    <TagInputSection
                      title={t("workspace:skills")}
                      inputId="create-programme-modal-input-skill"
                      addButtonId="create-programme-modal-add-skill"
                      value={currentSkill}
                      onChange={(e) => setCurrentSkill(e.target.value)}
                      onKeyDown={handleSkillKeyDown}
                      onPaste={(e) =>
                        handlePasteItems(e, setSkills, skills, true)
                      }
                      onAdd={handleAddSkill}
                      placeholder="e.g., JavaScript, React, Node.js"
                      items={skills}
                      onRemove={handleRemoveSkill}
                      tagIdPrefix="create-programme-modal-tag-skill"
                      removeIdPrefix="create-programme-modal-remove-skill"
                      emptyText={t("workspace:no_skills_added")}
                      hintText={t("workspace:add_skills_hint")}
                      addLabel={t("workspace:add")}
                    />
                  </div>

                  {errorMessage && (
                    <p
                      id="create-programme-modal-error-message"
                      className="mt-3 text-xs text-cfIssuedCredentialTemplateCardStatusFailedBG flex items-center"
                    >
                      <span className="mr-1 text-base leading-none">*</span>
                      {errorMessage.error} - {errorMessage.message}
                    </p>
                  )}

                  <div className="flex mt-4 border-cfIssuedScreenSearchBorder justify-end gap-3">
                    <button
                      type="button"
                      id="create-programme-modal-cancel"
                      className="px-4 py-2 text-sm text-cfHeadingText border hover:bg-cfCardHover transition-all border-cfHeadingText rounded-[8px] focus:outline-none focus:ring-3 focus:ring-offset-2"
                      onClick={handleClose}
                      disabled={isLoading}
                    >
                      {t("workspace:cancel")}
                    </button>
                    <button
                      type="submit"
                      id="create-programme-modal-submit"
                      className="px-4 py-2 text-sm bg-cfButton hover:bg-cfPrimaryDark text-cfSecondary font-semibold border transition-all border-cfHeadingText rounded-[8px] focus:outline-none focus:ring-3 focus:ring-offset-2 flex items-center justify-center min-w-[120px]"
                      disabled={isLoading}
                    >
                      {isLoading
                        ? isEditMode
                          ? t("workspace:updating")
                          : t("workspace:creating")
                        : `${
                            isEditMode
                              ? t("workspace:update")
                              : t("workspace:create")
                          } ${t("workspace:programme")}`}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
